/* eslint-disable max-lines */
import '@react-pdf-viewer/core/lib/styles/index.css';

import { zodResolver } from '@hookform/resolvers/zod';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import { useMutation } from '@tanstack/react-query';
import { CalendarDays, Clock, File, MapPin, Minus, Plus, Trash2 } from 'lucide-react';
import { FC, Fragment, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { axiosInstance } from 'src/api';
import { Error, Modal, Spinner } from 'src/components';
import { getLocalizedLangValue } from 'src/helpers';
import { useEventPlaces, useToast } from 'src/hooks';
import { useGetEventFromFirestore, useQueryHelper } from 'src/queries';
import { eventFormSchema } from 'src/schemas';
import { useLanguageState, useOrganizationState } from 'src/state';

import ErrorImage from '../../assets/images/link-sent-error.png';
import SuccesImage from '../../assets/images/link-sent-success.png';
import {
  ActionContainer,
  Banner,
  BannerContainer,
  Button,
  Container,
  CounterButton,
  CounterContainer,
  Description,
  Form,
  FormContainer,
  FormsTitle,
  FormWrapper,
  FreeAccess,
  FreePlaces,
  Input,
  InputsContainer,
  Label,
  LabelsContainer,
  LabelText,
  LoaderTitle,
  PdfContainer,
  PdfText,
  PersonContainer,
  PersonTitle,
  ScrollContainer,
  StatusImage,
  StatusText,
  Title,
  TrashButton,
} from './Event.styled';

type GuestForm = {
  firstName: string;
  roomNumber: string;
}

export const Event: FC = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { showError, showPromise } = useToast();
  const { language: currentLang } = useLanguageState();
  const { organizationData } = useOrganizationState();
  const EventDocument = useGetEventFromFirestore(organizationData.hotel, id || '');
  const { data: EventData, refetch } = EventDocument;
  const { isLoading, isError } = useQueryHelper({ queries: [EventDocument] });
  const [guestCount, setGuestCount] = useState(1);
  const [pdfModal, setPdfModal] = useState(false);
  const { places, freeAccess } = useEventPlaces({
    isOpen: EventData?.isOpen,
    remainingEntries: EventData?.remainingEntries,
  });

  const placesString = places();
  const placesNumber = parseInt(placesString || '', 10);

  const methods = useForm<GuestForm>({
    defaultValues: {
      firstName: '',
      roomNumber: '',
    },
    mode: 'onSubmit',
    resolver: zodResolver(eventFormSchema),
  });

  const { reset } = methods;

  const registerForEvent = async (data: {
    name: string;
    room: string;
    guests: number;
    createdAt: Date;
  }) => axiosInstance.post(
    `/EvenRegistration?hotelId=${organizationData.hotel}&eventId=${id}`,
    data,
    { headers: { 'Content-Type': 'application/json' } },
  );

  const eventMutation = useMutation({
    mutationFn: registerForEvent,
    onSuccess: () => { reset(); },
    onError: () => {
      showError('Błąd zapisu!');
    },
  });

  const onSubmit = (data: GuestForm) => {
    if (!data) return;

    const promise = eventMutation.mutateAsync({
      name: data.firstName,
      room: data.roomNumber,
      guests: guestCount,
      createdAt: new Date(),
    });

    showPromise(
      promise,
      {
        pending: 'Trwa zapis...',
        success: 'Dane przesłane poprawnie.',
        error: 'Błąd zapisu!',
      },
    );
  };

  if (isLoading) {
    return <Spinner global />;
  }

  if (isError) {
    return (<Error errorMessage="Bład pobierania atrakcji" action={refetch} />
    );
  }

  return (
    <>
      <Helmet>
        <title>Hotelspot | Szczegóły wydarzenia hotelowego</title>
        <meta name="description" content="Hotelspot - Szczegóły wydarzenia hotelowego" />
      </Helmet>
      <ScrollContainer>
        <Title>
          {EventData?.title[currentLang]}
        </Title>
        <BannerContainer>
          {
            !EventData?.isOpen
            && EventData?.remainingEntries
            && EventData?.registration
            && placesString && <FreePlaces>{placesString}</FreePlaces>
}
          {EventData?.isOpen && <FreeAccess>{t('events_screen.free_access')}</FreeAccess>}
          <Banner src={EventData?.coverPhoto || ''} />
        </BannerContainer>
        <LabelsContainer>
          <Label>
            <CalendarDays size={18} color="#1C1C1C" />
            <LabelText>{EventData?.eventDate}</LabelText>
          </Label>
          <Label>
            <Clock size={18} color="#1C1C1C" />
            <LabelText>{EventData?.eventTimeFrom} - {EventData?.eventTimeTo}</LabelText>
          </Label>
          <Label>
            <MapPin size={18} color="#1C1C1C" />
            <LabelText>{EventData?.localization?.[currentLang]}</LabelText>
          </Label>
        </LabelsContainer>
        {EventData?.attachment?.pl && (
          <LabelsContainer>
            <PdfContainer onClick={() => setPdfModal(true)}>
              <File size={18} color="#FFF" />
              <PdfText>Załącznik</PdfText>
            </PdfContainer>
          </LabelsContainer>
        )}
        <Description>
          {EventData?.description?.[currentLang]}
        </Description>
        {eventMutation.isPending && (
        <Container>
          <Spinner />
          <LoaderTitle>{t('event_screen.loading')}</LoaderTitle>
        </Container>
        )}
        {EventData?.isOpen === false
          && !eventMutation.isPending
          && !eventMutation.isSuccess
          && !eventMutation.isError
          && !freeAccess
          && EventData?.remainingEntries !== 'NaN'
          && (
          <FormContainer>
            <FormsTitle>{t('event_screen.save_on_event')}</FormsTitle>
            <Form onSubmit={methods.handleSubmit(onSubmit)}>
              <InputsContainer>
                <Input
                  type="text"
                  placeholder={t('event_screen.firstname')}
                  {...methods.register('firstName')}
                  error={methods.formState.errors.firstName?.message}
                />
                <Input
                  type="text"
                  placeholder={t('event_screen.room_number')}
                  {...methods.register('roomNumber')}
                  error={methods.formState.errors.roomNumber?.message}
                />
              </InputsContainer>
              <FormWrapper>
                <PersonContainer>
                  <PersonTitle>{t('event_screen.guest_number')}</PersonTitle>
                  <CounterContainer>
                    <CounterButton
                      type="button"
                      onClick={() => {
                        if (guestCount > 1) {
                          setGuestCount(guestCount - 1);
                        }
                      }}
                    ><Minus />
                    </CounterButton>
                    <PersonTitle>{guestCount}</PersonTitle>
                    <CounterButton
                      type="button"
                      onClick={() => {
                        if (guestCount < placesNumber) {
                          setGuestCount(guestCount + 1);
                        }
                      }}
                    ><Plus />
                    </CounterButton>
                  </CounterContainer>
                </PersonContainer>
                <ActionContainer>
                  <TrashButton onClick={() => reset()}><Trash2 /></TrashButton>
                  <Button type="submit">{t('event_screen.confirm')}</Button>
                </ActionContainer>
              </FormWrapper>
            </Form>
          </FormContainer>
          )}
        {eventMutation.isSuccess && (
        <Container>
          <StatusImage src={SuccesImage} alt="error" />
          <StatusText>{t('event_screen.success_saved')}</StatusText>
        </Container>
        )}
        {eventMutation.isError && (
        <Container>
          <StatusImage src={ErrorImage} alt="error" />
          <StatusText>{t('event_screen.error_saved')}</StatusText>
        </Container>
        )}
      </ScrollContainer>
      <Modal
        size="full"
        isOpen={pdfModal}
        onClose={() => {
          setPdfModal(false);
        }}
        title="Załącznik"
      >
        <Fragment key="modal">
          <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
            <Viewer
              fileUrl={EventData?.attachment?.pl ? getLocalizedLangValue(EventData?.attachment, currentLang) : ''}
              defaultScale={1}
            />
          </Worker>
        </Fragment>
      </Modal>
    </>
  );
};
